import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

// Maximum unique users allowed per time slot per day (bucket capacity)
const MAX_USERS_PER_SLOT = 10;

interface SlotAvailability {
  slot: string;
  booked: number;
  available: number;
  isFull: boolean;
}

interface UseSlotAvailabilityReturn {
  availability: Record<string, SlotAvailability>;
  loading: boolean;
  checkDateAvailability: (date: Date) => Promise<void>;
  isSlotAvailable: (slot: string) => boolean;
  getSlotStatus: (slot: string) => SlotAvailability | null;
}

export const useSlotAvailability = (): UseSlotAvailabilityReturn => {
  const [availability, setAvailability] = useState<Record<string, SlotAvailability>>({});
  const [loading, setLoading] = useState(false);

  const checkDateAvailability = useCallback(async (date: Date) => {
    setLoading(true);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      
      // Count distinct users per time slot on this date
      const { data: pickups, error } = await supabase
        .from("user_pickups")
        .select("proposed_time_slot, user_id")
        .eq("proposed_date", dateStr)
        .in("status", ["confirmed", "awaiting_admin_confirmation", "awaiting_confirmation", "counter_proposed"]);

      if (error) throw error;

      // Define all time slots (normalize to handle slight variations)
      const slots = [
        "9:00 AM - 12:00 PM",
        "12:00 PM - 3:00 PM",
        "3:00 PM - 6:00 PM",
      ];

      // Count bookings per slot
      const slotCounts: Record<string, number> = {};
      slots.forEach(slot => {
        slotCounts[slot] = 0;
      });

      // Count distinct users per slot
      const slotUsers: Record<string, Set<string>> = {};
      slots.forEach(slot => { slotUsers[slot] = new Set(); });

      pickups?.forEach(pickup => {
        const normalizedSlot = normalizeTimeSlot(pickup.proposed_time_slot);
        if (normalizedSlot && slotUsers[normalizedSlot]) {
          slotUsers[normalizedSlot].add(pickup.user_id);
        }
      });

      slots.forEach(slot => {
        slotCounts[slot] = slotUsers[slot].size;
      });

      // Build availability map
      const newAvailability: Record<string, SlotAvailability> = {};
      slots.forEach(slot => {
        const booked = slotCounts[slot];
        const available = Math.max(0, MAX_USERS_PER_SLOT - booked);
        newAvailability[slot] = {
          slot,
          booked,
          available,
          isFull: available === 0,
        };
      });

      setAvailability(newAvailability);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error checking slot availability:", error);
      }
      // Reset to empty on error
      setAvailability({});
    } finally {
      setLoading(false);
    }
  }, []);

  const isSlotAvailable = useCallback((slot: string): boolean => {
    const normalizedSlot = normalizeTimeSlot(slot);
    const slotData = availability[normalizedSlot];
    return slotData ? !slotData.isFull : true; // Default to available if not loaded
  }, [availability]);

  const getSlotStatus = useCallback((slot: string): SlotAvailability | null => {
    const normalizedSlot = normalizeTimeSlot(slot);
    return availability[normalizedSlot] || null;
  }, [availability]);

  return {
    availability,
    loading,
    checkDateAvailability,
    isSlotAvailable,
    getSlotStatus,
  };
};

// Helper to normalize time slot strings (handle different dash characters)
function normalizeTimeSlot(slot: string): string {
  if (!slot) return "";
  // Replace en-dash, em-dash with regular hyphen and normalize spacing
  return slot
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

export { MAX_USERS_PER_SLOT };
